import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Role, getRoleName } from '../../types/models';
import {
  LayoutDashboard,
  Wrench,
  UserCircle,
  Clock,
  FileText,
  Users,
  BarChart3,
  LogOut,
  Car,
} from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', roles: [Role.ADMIN, Role.JEFE_TALLER, Role.OPERARIO, Role.RECEPCIONISTA] },
    { path: '/reception', icon: Car, label: 'Recepción', roles: [Role.RECEPCIONISTA, Role.JEFE_TALLER] },
    { path: '/workshop', icon: Wrench, label: 'Taller', roles: [Role.JEFE_TALLER, Role.OPERARIO] },
    { path: '/my-work', icon: UserCircle, label: 'Mi Trabajo', roles: [Role.OPERARIO] },
    { path: '/attendance', icon: Clock, label: 'Asistencia', roles: [Role.ADMIN, Role.JEFE_TALLER, Role.OPERARIO, Role.RECEPCIONISTA] },
    { path: '/permissions', icon: FileText, label: 'Permisos', roles: [Role.ADMIN, Role.JEFE_TALLER, Role.OPERARIO, Role.RECEPCIONISTA] },
    { path: '/users', icon: Users, label: 'Usuarios', roles: [Role.ADMIN] },
    { path: '/reports', icon: BarChart3, label: 'Reportes', roles: [Role.ADMIN, Role.JEFE_TALLER] },
  ];

  const filteredNavItems = navItems.filter(item => hasRole(item.roles as Role[]));

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-slate-800 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Wrench className="h-8 w-8" />
              <div>
                <h1 className="text-xl font-bold">TimeTrex Taller</h1>
                <p className="text-xs text-slate-400">Gestión de Taller Automotriz</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-slate-400">{getRoleName(user?.role_id as Role)}</p>
              </div>
              <button
                onClick={logout}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                title="Cerrar sesión"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className="w-64 bg-white shadow-md min-h-[calc(100vh-64px)]">
          <ul className="py-4">
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-600 border-r-4 border-blue-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
